import { Button } from "@/components/ui/button.tsx";
import { Card } from "@/components/ui/card.tsx";
import { ExclamationTriangleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import type { ReactElement, ReactNode } from "react";
import { cn } from "cn";
import { PageTitle } from "../components/PageTitle.tsx";

export type ErrorStateKind = "load" | "notFound" | "denied";

export interface ErrorStateProps {
    kind?: ErrorStateKind;
    title: string;
    description?: string;
    ownsPageTitle?: boolean;
    action?: ReactNode;
    isEmbedded?: boolean;
    onRetry?: () => void;
    retryLabel?: string;
    isRetrying?: boolean;
    retryingLabel?: string;
    className?: string;
}

function getKindIcon(kind: ErrorStateKind): ReactElement | null {
    if (kind === "load") {
        return <ExclamationTriangleIcon />;
    }

    if (kind === "denied") {
        return <LockClosedIcon />;
    }

    return null;
}

export function ErrorState({ kind = "load", title, description, ownsPageTitle = false, action, isEmbedded = false, className, onRetry, retryLabel, isRetrying = false, retryingLabel }: ErrorStateProps) {
    const icon = getKindIcon(kind);
    const hasRetry = Boolean(onRetry && retryLabel);
    const role = kind === "load" ? "alert" : "status";

    const content = (
        <>
            {icon ? (
                <span aria-hidden="true" className={cn("flex size-12 items-center justify-center rounded-2xl [&>svg]:size-6", kind === "load" ? "bg-danger/10 text-danger" : "bg-muted text-muted-fg")}>
                    {icon}
                </span>
            ) : null}
            {ownsPageTitle ? <PageTitle>{title}</PageTitle> : <p className="text-fg text-sm font-medium">{title}</p>}
            {description ? <p className="text-muted-fg text-sm">{description}</p> : null}
            {hasRetry || action ? (
                <div className="mt-1 flex items-center gap-2">
                    {hasRetry ? (
                        <Button intent="secondary" isPending={isRetrying} onPress={onRetry}>
                            {isRetrying && retryingLabel ? retryingLabel : retryLabel}
                        </Button>
                    ) : null}
                    {action}
                </div>
            ) : null}
        </>
    );

    const layout = "flex flex-col items-center justify-center gap-3 px-12 py-10 text-center";

    if (isEmbedded) {
        return (
            <div role={role} className={cn(layout, "h-full", className)}>
                {content}
            </div>
        );
    }

    return (
        <Card role={role} className={cn(layout, "min-h-72", className)}>
            {content}
        </Card>
    );
}
